import { doc, getDoc, setDoc, getFirestore, serverTimestamp, collection, getDocs, query, where } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { CONT_COLLECTIONS } from "./collections";
import { generarLibroDiario } from "./asientosService";
import { obtenerCuenta, validarCuentaActiva } from "./accountingCore";
import { conciliarSireContabilidad } from "./sireConciliacionContableService";

const db = getFirestore(firebaseApp);

const cleanText = (v) => (v == null ? "" : String(v).trim());
const cleanNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Calculates the Trial Balance (Balance de Comprobación) for a period.
 */
export const generarBalanceComprobacion = async ({ rucEmpresa, periodo }) => {
  const ruc = cleanText(rucEmpresa);
  const per = cleanText(periodo);

  if (!ruc || !per) {
    throw new Error("RUC y Periodo son obligatorios para generar el Balance de Comprobación.");
  }

  const asientos = await generarLibroDiario({ rucEmpresa: ruc, periodo: per });
  const accountsMap = {};

  for (const seat of asientos) {
    if (!seat.cuo) continue; // Skip legacy seats without CUO from active balance calculation

    // Debt lines
    for (const line of seat.debe || []) {
      const code = cleanText(line.cuenta);
      if (!accountsMap[code]) {
        accountsMap[code] = { debe: 0, haber: 0 };
      }
      accountsMap[code].debe += cleanNumber(line.monto);
    }

    // Credit lines
    for (const line of seat.haber || []) {
      const code = cleanText(line.cuenta);
      if (!accountsMap[code]) {
        accountsMap[code] = { debe: 0, haber: 0 };
      }
      accountsMap[code].haber += cleanNumber(line.monto);
    }
  }

  const rows = [];
  let sumDebe = 0;
  let sumHaber = 0;
  let sumSaldoDeudor = 0;
  let sumSaldoAcreedor = 0;

  for (const [code, totals] of Object.entries(accountsMap)) {
    const accDoc = await obtenerCuenta(ruc, code);
    const desc = accDoc ? cleanText(accDoc.descripcion || accDoc.nombre) : "Cuenta Contable";

    const debeVal = totals.debe;
    const haberVal = totals.haber;

    let saldoDeudor = 0;
    let saldoAcreedor = 0;

    // Check account nature: Deudora vs Acreedora
    if (code.startsWith("1") || code.startsWith("2") || code.startsWith("3") || code.startsWith("6") || code.startsWith("9")) {
      saldoDeudor = Math.max(0, debeVal - haberVal);
      saldoAcreedor = Math.max(0, haberVal - debeVal);
    } else {
      saldoAcreedor = Math.max(0, haberVal - debeVal);
      saldoDeudor = Math.max(0, debeVal - haberVal);
    }

    sumDebe += debeVal;
    sumHaber += haberVal;
    sumSaldoDeudor += saldoDeudor;
    sumSaldoAcreedor += saldoAcreedor;

    rows.push({
      cuenta: code,
      denominacion: desc,
      debe: debeVal,
      haber: haberVal,
      saldoDeudor,
      saldoAcreedor,
    });
  }

  const diff = Math.abs(sumDebe - sumHaber);
  const balanceado = diff < 0.01;

  return {
    rows,
    sumDebe,
    sumHaber,
    sumSaldoDeudor,
    sumSaldoAcreedor,
    balanceado,
  };
};

/**
 * Assesses contable health before closure. Detects inconsistencies or orphans.
 */
export const evaluarSaludContable = async ({ rucEmpresa, periodo }) => {
  const ruc = cleanText(rucEmpresa);
  const per = cleanText(periodo);

  const findings = [];
  let severeCount = 0;

  const asientos = await generarLibroDiario({ rucEmpresa: ruc, periodo: per });

  // 1. Check legacy seats without CUO
  const seatsWithoutCuo = asientos.filter((a) => !a.cuo);
  if (seatsWithoutCuo.length > 0) {
    severeCount++;
    findings.push({
      id: "HEALTH_LEGACY_NO_CUO",
      severidad: "ALTA",
      modulo: "CONTABILIDAD",
      mensaje: `Existen ${seatsWithoutCuo.length} asientos históricos sin CUO.`,
    });
  }

  // 2. Check unbalanced entries
  for (const seat of asientos) {
    if (!seat.cuo) continue;
    const debeTotal = (seat.debe || []).reduce((acc, x) => acc + cleanNumber(x.monto), 0);
    const haberTotal = (seat.haber || []).reduce((acc, x) => acc + cleanNumber(x.monto), 0);
    if (Math.abs(debeTotal - haberTotal) > 0.01) {
      severeCount++;
      findings.push({
        id: "HEALTH_UNBALANCED_ENTRY",
        severidad: "CRITICA",
        modulo: "CONTABILIDAD",
        asientoId: seat.id,
        cuo: seat.cuo,
        mensaje: `Asiento con CUO ${seat.cuo} desbalanceado (Debe: ${debeTotal}, Haber: ${haberTotal}).`,
      });
    }

    // 3. Check invalid accounts in PCGE
    const lines = [...(seat.debe || []), ...(seat.haber || [])];
    for (const line of lines) {
      const code = cleanText(line.cuenta);
      const active = await validarCuentaActiva(ruc, code);
      if (!active) {
        severeCount++;
        findings.push({
          id: "HEALTH_INVALID_ACCOUNT",
          severidad: "CRITICA",
          modulo: "CONTABILIDAD",
          cuenta: code,
          cuo: seat.cuo,
          mensaje: `La cuenta ${code} del asiento CUO ${seat.cuo} no está activa en el PCGE.`,
        });
      }
    }
  }

  // 4. Orphan check: Search documents in os_comprobantes_venta
  // Fetching all registered invoices/tickets in the period
  const qVentas = query(
    collection(db, "os_comprobantes_venta"),
    where("ruc_empresa", "==", ruc),
    where("periodo", "==", per)
  );
  const snapVentas = await getDocs(qVentas);
  const totalVentas = snapVentas.docs.length;

  // Verify if sales have corresponding seats
  snapVentas.docs.forEach((docSnap) => {
    const saleId = docSnap.id;
    const hasSeat = asientos.some((a) => a.comprobante_id === saleId || a.glosa?.includes(saleId));
    if (!hasSeat) {
      findings.push({
        id: "HEALTH_ORPHAN_SALE",
        severidad: "MEDIA",
        modulo: "VENTAS",
        documentoId: saleId,
        mensaje: `La venta ${saleId} no tiene un asiento contable asociado.`,
      });
    }
  });

  // 5. Tax Reconciliation Check
  const taxReconc = await conciliarSireContabilidad({ rucEmpresa: ruc, periodo: per });
  if (taxReconc.status === "NO CONCILIADO") {
    taxReconc.findings.forEach((f) => {
      severeCount++;
      findings.push({
        id: f.id,
        severidad: f.severidad,
        modulo: "TRIBUTARIO",
        mensaje: f.mensaje,
      });
    });
  }

  const status = severeCount > 0 ? "CONTABILIDAD_NO_APTA_PARA_CIERRE" : "CONTABILIDAD_APTA_PARA_CIERRE";

  return {
    status,
    findings,
    severeCount,
    totalAsientos: asientos.length,
    totalVentas,
  };
};

/**
 * Locks the period as CERRADO for the GENERAL type.
 */
export const ejecutarCierreContable = async ({ rucEmpresa, periodo, usuario, motivo }) => {
  const ruc = cleanText(rucEmpresa);
  const per = cleanText(periodo);

  const health = await evaluarSaludContable({ rucEmpresa: ruc, periodo: per });
  if (health.status === "CONTABILIDAD_NO_APTA_PARA_CIERRE") {
    throw new Error("CIERRE_BLOCKED_HEALTH_FAIL: La contabilidad contiene inconsistencias críticas y no puede cerrarse.");
  }

  const docId = `${ruc}_${per}_GENERAL`;
  const docRef = doc(db, CONT_COLLECTIONS.SIRE_CIERRES, docId);

  const payload = {
    ruc_empresa: ruc,
    periodo: per,
    tipo: "GENERAL",
    estado: "CERRADO",
    cerrado_at: serverTimestamp(),
    cerrado_por: cleanText(usuario),
    motivo_cierre: cleanText(motivo || "Cierre mensual contable consolidado"),
  };

  await setDoc(docRef, payload, { merge: true });
  return { id: docId, ...payload };
};

/**
 * Unlocks the period back to ABIERTO.
 */
export const ejecutarReaperturaContable = async ({ rucEmpresa, periodo, usuario, motivo, rol }) => {
  const ruc = cleanText(rucEmpresa);
  const per = cleanText(periodo);
  const cleanRol = cleanText(rol);

  if (cleanRol !== "Administrador" && cleanRol !== "Contador") {
    throw new Error("REAPERTURA_UNAUTHORIZED: Sólo administradores o contadores pueden autorizar reaperturas.");
  }

  if (!cleanText(motivo)) {
    throw new Error("REAPERTURA_MOTIVO_OBLIGATORIO: Se requiere un motivo obligatorio para abrir el período.");
  }

  const docId = `${ruc}_${per}_GENERAL`;
  const docRef = doc(db, CONT_COLLECTIONS.SIRE_CIERRES, docId);

  const payload = {
    ruc_empresa: ruc,
    periodo: per,
    tipo: "GENERAL",
    estado: "ABIERTO",
    reabierto_at: serverTimestamp(),
    reabierto_por: cleanText(usuario),
    motivo_reapertura: cleanText(motivo),
    autorizado_rol: cleanRol,
  };

  await setDoc(docRef, payload, { merge: true });
  return { id: docId, ...payload };
};

/**
 * Calculates next period's initial balances.
 */
export const aperturarPeriodoSiguiente = async ({ rucEmpresa, periodoActual, usuario }) => {
  const ruc = cleanText(rucEmpresa);
  const per = cleanText(periodoActual);

  // Compute next period string YYYY-MM
  const [yyyy, mm] = per.split("-").map(Number);
  let nextYear = yyyy;
  let nextMonth = mm + 1;
  if (nextMonth > 12) {
    nextMonth = 1;
    nextYear += 1;
  }
  const nextPeriod = `${nextYear}-${String(nextMonth).padStart(2, "0")}`;

  const balance = await generarBalanceComprobacion({ rucEmpresa: ruc, periodo: per });

  const initialBalances = balance.rows.map((r) => {
    const finalBalance = r.saldoDeudor - r.saldoAcreedor;
    return {
      cuenta: r.cuenta,
      denominacion: r.denominacion,
      saldoInicial: finalBalance,
    };
  });

  return {
    periodoOrigen: per,
    periodoDestino: nextPeriod,
    initialBalances,
    generadoPor: cleanText(usuario),
    timestamp: new Date().toISOString(),
  };
};
